/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BusinessCardDto } from '../models/business-card-dto';
import { RelationTypeEnum } from '../models/relation-type-enum';

@Injectable({
  providedIn: 'root',
})
export class OrgchartService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getOrgChart
   */
  static readonly GetOrgChartPath = '/orgchart/{personId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getOrgChart()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrgChart$Response(params: {
    personId: string;
    relationTypes: Array<RelationTypeEnum>;
    companyId: string;
  }): Observable<StrictHttpResponse<BusinessCardDto>> {

    const rb = new RequestBuilder(this.rootUrl, OrgchartService.GetOrgChartPath, 'get');
    if (params) {
      rb.path('personId', params.personId, {});
      rb.query('relationTypes', params.relationTypes, {"style":"form","explode":false});
      rb.query('companyId', params.companyId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BusinessCardDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getOrgChart$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getOrgChart(params: {
    personId: string;
    relationTypes: Array<RelationTypeEnum>;
    companyId: string;
  }): Observable<BusinessCardDto> {

    return this.getOrgChart$Response(params).pipe(
      map((r: StrictHttpResponse<BusinessCardDto>) => r.body as BusinessCardDto)
    );
  }

  /**
   * Path part for operation getCompanyOrgChart
   */
  static readonly GetCompanyOrgChartPath = '/orgchart/company/{companyId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCompanyOrgChart()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCompanyOrgChart$Response(params: {
    companyId: string;
  }): Observable<StrictHttpResponse<BusinessCardDto>> {

    const rb = new RequestBuilder(this.rootUrl, OrgchartService.GetCompanyOrgChartPath, 'get');
    if (params) {
      rb.path('companyId', params.companyId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BusinessCardDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getCompanyOrgChart$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCompanyOrgChart(params: {
    companyId: string;
  }): Observable<BusinessCardDto> {

    return this.getCompanyOrgChart$Response(params).pipe(
      map((r: StrictHttpResponse<BusinessCardDto>) => r.body as BusinessCardDto)
    );
  }

  /**
   * Path part for operation createOrgRelation
   */
  static readonly CreateOrgRelationPath = '/orgchart/relation/{personId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createOrgRelation()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOrgRelation$Response(params: {

    /**
     * the person
     */
    personId: string;
    companyId: string;

    /**
     * collection of person ids along with SUBORDINATE or MANAGER relationType
     */
    body?: Array<BusinessCardDto>
  }): Observable<StrictHttpResponse<BusinessCardDto>> {

    const rb = new RequestBuilder(this.rootUrl, OrgchartService.CreateOrgRelationPath, 'post');
    if (params) {
      rb.path('personId', params.personId, {});
      rb.query('companyId', params.companyId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BusinessCardDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createOrgRelation$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createOrgRelation(params: {

    /**
     * the person
     */
    personId: string;
    companyId: string;

    /**
     * collection of person ids along with SUBORDINATE or MANAGER relationType
     */
    body?: Array<BusinessCardDto>
  }): Observable<BusinessCardDto> {

    return this.createOrgRelation$Response(params).pipe(
      map((r: StrictHttpResponse<BusinessCardDto>) => r.body as BusinessCardDto)
    );
  }

  /**
   * Path part for operation deleteOrgRelation
   */
  static readonly DeleteOrgRelationPath = '/orgchart/relation';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteOrgRelation()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteOrgRelation$Response(params: {
    personFromId: string;
    personToId: string;
    companyId: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrgchartService.DeleteOrgRelationPath, 'delete');
    if (params) {
      rb.query('personFromId', params.personFromId, {});
      rb.query('personToId', params.personToId, {});
      rb.query('companyId', params.companyId, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteOrgRelation$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteOrgRelation(params: {
    personFromId: string;
    personToId: string;
    companyId: string;
  }): Observable<void> {

    return this.deleteOrgRelation$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation upsertPerson
   */
  static readonly UpsertPersonPath = '/orgchart/person/upsert';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `upsertPerson()` instead.
   *
   * This method doesn't expect any request body.
   */
  upsertPerson$Response(params: {
    existingPerson?: string;
    newPerson: string;
    companyId: string;
    asCeo: boolean;
  }): Observable<StrictHttpResponse<BusinessCardDto>> {

    const rb = new RequestBuilder(this.rootUrl, OrgchartService.UpsertPersonPath, 'patch');
    if (params) {
      rb.query('existingPerson', params.existingPerson, {});
      rb.query('newPerson', params.newPerson, {});
      rb.query('companyId', params.companyId, {});
      rb.query('asCeo', params.asCeo, {});
    }

    return this.http.request(rb.build({
      responseType: 'blob',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BusinessCardDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `upsertPerson$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  upsertPerson(params: {
    existingPerson?: string;
    newPerson: string;
    companyId: string;
    asCeo: boolean;
  }): Observable<BusinessCardDto> {

    return this.upsertPerson$Response(params).pipe(
      map((r: StrictHttpResponse<BusinessCardDto>) => r.body as BusinessCardDto)
    );
  }

  /**
   * Path part for operation mockData
   */
  static readonly MockDataPath = '/orgchart/mockdata';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `mockData()` instead.
   *
   * This method doesn't expect any request body.
   */
  mockData$Response(params?: {
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, OrgchartService.MockDataPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `mockData$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  mockData(params?: {
  }): Observable<void> {

    return this.mockData$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
